import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import {
  House, Hammer, HardHat, Briefcase, ChatCircleDots, Storefront,
  UserCircle, MagnifyingGlass, PaperPlaneTilt, Wrench, Sparkle, ShoppingCartSimple,
} from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;

const ROLE_OPTIONS = [
  { key: 'homeowner', label: 'Homeowner', desc: 'I want to hire pros for my projects', icon: House, color: 'bg-[#2B4C6F]' },
  { key: 'contractor', label: 'Contractor', desc: 'I run jobs and hire skilled workers', icon: Hammer, color: 'bg-[#D95800]' },
  { key: 'worker', label: 'Worker', desc: 'I want to find work in the trades', icon: HardHat, color: 'bg-emerald-700' },
];

const MENU = {
  homeowner: [
    { label: 'AI Project Chat', icon: Sparkle, to: '/ai-project', color: 'bg-violet-700' },
    { label: 'Post a Project', icon: PaperPlaneTilt, to: '/get-quotes', color: 'bg-[#D95800]' },
    { label: 'Find Contractors', icon: MagnifyingGlass, to: '/find-workers', color: 'bg-[#2B4C6F]' },
    { label: 'My Projects', icon: Briefcase, to: '/homeowner-dashboard', color: 'bg-emerald-700' },
    { label: 'Messages', icon: ChatCircleDots, to: '/messages', color: 'bg-amber-600' },
    { label: 'Marketplace', icon: Storefront, to: '/marketplace', color: 'bg-neutral-700' },
  ],
  contractor: [
    { label: 'Find Workers', icon: MagnifyingGlass, to: '/find-workers', color: 'bg-[#D95800]' },
    { label: 'Browse Jobs', icon: Briefcase, to: '/browse-jobs', color: 'bg-[#2B4C6F]' },
    { label: 'My Dashboard', icon: Hammer, to: '/contractor-dashboard', color: 'bg-emerald-700' },
    { label: 'Messages', icon: ChatCircleDots, to: '/messages', color: 'bg-violet-700' },
    { label: 'Marketplace', icon: Storefront, to: '/marketplace', color: 'bg-amber-600' },
    { label: 'Tool Store', icon: ShoppingCartSimple, to: '/store', color: 'bg-[#1C93E3]' },
    { label: 'My Account', icon: UserCircle, to: '/contractor-dashboard', color: 'bg-neutral-700' },
  ],
  worker: [
    { label: 'Browse Jobs', icon: Briefcase, to: '/browse-jobs', color: 'bg-[#D95800]' },
    { label: 'My Dashboard', icon: HardHat, to: '/worker-dashboard', color: 'bg-[#2B4C6F]' },
    { label: 'Messages', icon: ChatCircleDots, to: '/messages', color: 'bg-violet-700' },
    { label: 'Marketplace', icon: Storefront, to: '/marketplace', color: 'bg-amber-600' },
    { label: 'Tool Store', icon: ShoppingCartSimple, to: '/store', color: 'bg-[#1C93E3]' },
    { label: 'My Account', icon: UserCircle, to: '/worker-dashboard', color: 'bg-neutral-700' },
  ],
};

export default function TelegramApp() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [phase, setPhase] = useState('loading'); // loading | not-telegram | pick-role | home | error
  const [tgUser, setTgUser] = useState(null);
  const [user, setUser] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');
  const [botUsername, setBotUsername] = useState('Constructhelp123bot');
  const [showLogin, setShowLogin] = useState(false);
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [loginBusy, setLoginBusy] = useState(false);

  const storeSession = useCallback((data, next) => {
    localStorage.setItem('token', data.token);
    localStorage.setItem('userType', data.user.user_type);
    localStorage.setItem('userId', data.user._id);
    localStorage.setItem('user', JSON.stringify(data.user));
    setUser(data.user);
    if (next) navigate(next);
    else setPhase('home');
  }, [navigate]);

  const authenticate = useCallback(async (userType) => {
    const tg = window.Telegram?.WebApp;
    const initData = tg?.initData;
    if (!initData) {
      axios.get(`${API}/api/telegram/bot-info`).then((r) => setBotUsername(r.data.bot_username)).catch(() => {});
      setPhase('not-telegram');
      return;
    }
    try {
      const res = await axios.post(`${API}/api/telegram/auth`, {
        init_data: initData,
        ...(userType ? { user_type: userType } : {}),
      });
      if (res.data.needs_role) {
        setTgUser(res.data.telegram_user);
        setPhase('pick-role');
        return;
      }
      storeSession(res.data, searchParams.get('next'));
    } catch (e) {
      setErrorMsg(e.response?.data?.detail || 'Authentication failed');
      setPhase('error');
    }
  }, [storeSession, searchParams]);

  const linkExisting = async () => {
    setLoginBusy(true);
    setLoginError('');
    try {
      const res = await axios.post(`${API}/api/telegram/auth-link`, {
        init_data: window.Telegram?.WebApp?.initData,
        email: loginEmail.trim(),
        password: loginPassword,
      });
      storeSession(res.data, searchParams.get('next'));
    } catch (e) {
      setLoginError(e.response?.data?.detail || 'Sign in failed');
    } finally {
      setLoginBusy(false);
    }
  };

  useEffect(() => {
    const tg = window.Telegram?.WebApp;
    if (tg) {
      tg.ready();
      tg.expand();
      try { tg.setHeaderColor('#0F172A'); } catch (e) { /* older clients */ }
    }
    authenticate();
  }, [authenticate]);

  if (phase === 'loading') {
    return (
      <div className="min-h-screen bg-[#0F172A] flex flex-col items-center justify-center gap-4" data-testid="tg-loading">
        <Wrench size={44} weight="duotone" className="text-[#D95800] animate-bounce" />
        <p className="text-white font-black uppercase tracking-widest text-sm">ConstructHelp</p>
        <div className="animate-spin w-8 h-8 border-4 border-[#D95800] border-t-transparent rounded-full" />
      </div>
    );
  }

  if (phase === 'not-telegram') {
    return (
      <div className="min-h-screen bg-[#0F172A] flex items-center justify-center px-6" data-testid="tg-not-telegram">
        <div className="text-center max-w-sm">
          <Wrench size={48} weight="duotone" className="text-[#D95800] mx-auto mb-4" />
          <h1 className="text-white font-black uppercase text-2xl leading-tight">ConstructHelp for Telegram</h1>
          <p className="text-neutral-400 text-sm mt-3">This page is designed to run inside Telegram as a Mini App. Open our bot and tap "Open ConstructHelp".</p>
          <a
            href={`https://t.me/${botUsername}`}
            className="inline-block mt-6 bg-[#D95800] text-white font-black uppercase text-sm px-6 py-3 border-2 border-black rounded-xl"
            data-testid="tg-open-bot-link"
          >
            Open @{botUsername}
          </a>
          <p className="text-neutral-500 text-xs mt-4">
            Or just use the website — <a href="/" className="text-[#D95800] underline">constructhelp.com</a>
          </p>
        </div>
      </div>
    );
  }

  if (phase === 'pick-role') {
    if (showLogin) {
      return (
        <div className="min-h-screen bg-[#0F172A] px-5 py-10" data-testid="tg-link-login">
          <div className="max-w-sm mx-auto">
            <Wrench size={36} weight="duotone" className="text-[#D95800] mb-3" />
            <h1 className="text-white font-black uppercase text-2xl leading-tight">Welcome back! 👋</h1>
            <p className="text-neutral-400 text-sm mt-2 mb-6">Sign in once and we'll connect this Telegram to your existing ConstructHelp account.</p>
            <div className="space-y-3">
              <input
                type="email" value={loginEmail} onChange={(e) => setLoginEmail(e.target.value)}
                placeholder="Email"
                className="w-full bg-white border-2 border-black rounded-xl px-4 py-3 text-sm font-semibold text-neutral-900 placeholder:text-neutral-400 focus:outline-none"
                data-testid="tg-link-email-input"
              />
              <input
                type="password" value={loginPassword} onChange={(e) => setLoginPassword(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && linkExisting()}
                placeholder="Password"
                className="w-full bg-white border-2 border-black rounded-xl px-4 py-3 text-sm font-semibold text-neutral-900 placeholder:text-neutral-400 focus:outline-none"
                data-testid="tg-link-password-input"
              />
              {loginError && <p className="text-red-400 text-xs font-bold" data-testid="tg-link-error">{loginError}</p>}
              <button
                onClick={linkExisting}
                disabled={loginBusy || !loginEmail || !loginPassword}
                className="w-full bg-[#D95800] text-white font-black uppercase text-sm py-3.5 border-2 border-black rounded-xl shadow-[4px_4px_0px_#000] disabled:opacity-50"
                data-testid="tg-link-submit-btn"
              >
                {loginBusy ? 'Connecting...' : 'Sign In & Connect'}
              </button>
              <button onClick={() => setShowLogin(false)} className="w-full text-neutral-400 text-xs font-bold uppercase py-2" data-testid="tg-link-back-btn">
                ← Back
              </button>
            </div>
          </div>
        </div>
      );
    }
    return (
      <div className="min-h-screen bg-[#0F172A] px-5 py-10" data-testid="tg-role-picker">
        <div className="max-w-sm mx-auto">
          <Wrench size={36} weight="duotone" className="text-[#D95800] mb-3" />
          <h1 className="text-white font-black uppercase text-2xl leading-tight">
            Welcome{tgUser?.first_name ? `, ${tgUser.first_name}` : ''}! 👋
          </h1>
          <p className="text-neutral-400 text-sm mt-2 mb-6">One quick question and you're in — what brings you to ConstructHelp?</p>
          <div className="space-y-3">
            {ROLE_OPTIONS.map((r) => {
              const Icon = r.icon;
              return (
                <button
                  key={r.key}
                  onClick={() => { setPhase('loading'); authenticate(r.key); }}
                  className="w-full flex items-center gap-4 bg-white border-2 border-black rounded-xl p-4 shadow-[4px_4px_0px_#D95800] active:translate-y-0.5 transition-transform text-left"
                  data-testid={`tg-role-${r.key}`}
                >
                  <span className={`${r.color} w-12 h-12 flex items-center justify-center border-2 border-black rounded-lg shrink-0`}>
                    <Icon size={24} weight="bold" className="text-white" />
                  </span>
                  <span>
                    <span className="block font-black uppercase text-neutral-900">{r.label}</span>
                    <span className="block text-xs text-neutral-500">{r.desc}</span>
                  </span>
                </button>
              );
            })}
          </div>
          <button
            onClick={() => setShowLogin(true)}
            className="w-full mt-5 text-center text-[#1C93E3] text-sm font-black uppercase underline underline-offset-4"
            data-testid="tg-existing-account-btn"
          >
            I already have an account — Sign in & connect
          </button>
        </div>
      </div>
    );
  }

  if (phase === 'error') {
    return (
      <div className="min-h-screen bg-[#0F172A] flex items-center justify-center px-6" data-testid="tg-error">
        <div className="text-center max-w-sm">
          <p className="text-white font-black uppercase text-xl">Couldn't sign you in</p>
          <p className="text-neutral-400 text-sm mt-2">{errorMsg}</p>
          <button onClick={() => { setPhase('loading'); authenticate(); }} className="mt-5 bg-[#D95800] text-white font-black uppercase text-sm px-6 py-3 border-2 border-black rounded-xl" data-testid="tg-retry-btn">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  const menu = MENU[user?.user_type] || MENU.worker;
  return (
    <div className="min-h-screen bg-[#0F172A]" data-testid="tg-home">
      <div className="max-w-sm mx-auto px-5 py-8">
        <div className="flex items-center gap-3 mb-1">
          <Wrench size={28} weight="duotone" className="text-[#D95800]" />
          <h1 className="text-white font-black uppercase tracking-tight text-xl">ConstructHelp</h1>
        </div>
        <p className="text-neutral-400 text-sm mb-6" data-testid="tg-welcome-text">
          Hey {user?.name?.split(' ')[0] || 'there'} — what do you need today?
        </p>
        <div className="grid grid-cols-2 gap-3">
          {menu.map((m) => {
            const Icon = m.icon;
            return (
              <button
                key={m.label}
                onClick={() => navigate(m.to)}
                className="bg-white border-2 border-black rounded-xl p-4 shadow-[4px_4px_0px_#D95800] active:translate-y-0.5 transition-transform text-left"
                data-testid={`tg-menu-${m.to.replace(/\//g, '') }-${m.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <span className={`${m.color} w-11 h-11 flex items-center justify-center border-2 border-black rounded-lg mb-3`}>
                  <Icon size={22} weight="bold" className="text-white" />
                </span>
                <span className="block font-black uppercase text-[13px] text-neutral-900 leading-tight">{m.label}</span>
              </button>
            );
          })}
        </div>
        <p className="text-neutral-600 text-[11px] text-center mt-8">
          Same account works at constructhelp.com · Notifications arrive right here in Telegram
        </p>
      </div>
    </div>
  );
}
